<script setup lang="ts">
/**
 * LumiShell — the shared chrome for the Lumi assistant (GitLab issue 137).
 *
 * PRESENTATIONAL ONLY. This owns the look + interaction-state of the assistant
 * window across two axes — mode (`float` | `dock`) × state (`expanded` |
 * `minimized`). It does NOT know about the agent API, polling, conversations
 * or approvals: the consuming app keeps that business logic and injects
 * messages/input/actions through the body/footer slots (the same "share the
 * shell, not the data" rule as the notices rollout).
 *
 *   ┌─────────┬──────────────────────────────────┬───────────────────────────┐
 *   │  Mode   │ Expanded                         │ Minimized                 │
 *   ├─────────┼──────────────────────────────────┼───────────────────────────┤
 *   │ float   │ 400×620 window, bottom-right     │ nothing (0 width)         │
 *   │ dock    │ 320px peer panel, reflows content│ nothing (0 width)         │
 *   └─────────┴──────────────────────────────────┴───────────────────────────┘
 *
 * This component renders NO entry point of its own. The way in, in every
 * mode and at every breakpoint, is the account row's Lumi button in
 * Sidebar.vue, reached through the `lumiDockLauncher` bridge — on a phone,
 * where that row sits in the closed drawer, also AppShell's app bar, which
 * drives the same bridge. That button
 * stays put while the panel is open (the bridge is `visible` for as long as a
 * LumiShell is mounted, and carries `expanded` so the button can render its
 * open state), so clicking it again is what closes Lumi — `toggle`, not
 * `expand`. Minimized therefore renders nothing and reserves no width in
 * either mode.
 *
 * The floating launcher pill has been retired twice now (a dock rail before
 * it): it sat over the bottom-right corner of every page — full-bleed
 * canvases and form action bars included — duplicating the sidebar button.
 * lumi-shell.spec pins that it stays gone.
 *
 * Reflow is achieved structurally: in `dock` the root sits in-flow inside
 * AppShell's `#dock` region and reserves width only while expanded, so the
 * content area shrinks and the panel reads as a peer of the main content
 * panel — same edge, radius and canvas margin, but its own whiter lavender
 * (`--lumi-panel`, #545) so the two never read as one surface. In `float` the
 * root collapses to 0 width and the window is `position:fixed`, reserving
 * nothing.
 * Below ~768px dock degrades to a full-screen sheet.
 *
 * Head and foot never draw a hairline against the thread: each sits on the
 * panel colour and fades into the scrolling body (spec §6), the same
 * fade-and-elevation pattern Modal/Drawer/Workbench use.
 *
 * The panel's width is the user's (#539): a drag handle on its left edge
 * (keyboard: arrows on the focused handle, double-click resets) resizes it
 * between LUMI_MIN_W and LUMI_MAX_W — never past half the viewport, so the
 * docked panel cannot squeeze the page away. The width lives in the browser
 * only, next to the mode (`<storageKey>:width`), and applies to both modes by
 * overriding their width tokens on the root.
 *
 * The float window's place is the user's too: its head is the handle it is
 * dragged by (the head's buttons stay buttons), anywhere inside the viewport.
 * It opens in its corner as before; once moved it stays there, through closing
 * and reopening, until a reload or a double-click on the head brings it back.
 * The place is held in memory only, not stored. A moved window stays anchored
 * on the right, so the resize handle keeps widening it leftwards.
 *
 * Mode persists in localStorage (per tenant+user via `storageKey`); the
 * conversation does NOT persist across modules — that's the app's per-module
 * agent client, intentionally fresh.
 *
 * Float mode's window also shifts up off the bottom-right corner
 * whenever a form's own action bar has reserved it (make brief
 * ai-switch-notify-launcher, outcome invariant 8) — see formActionBar.ts for
 * why the overlay z-index ladder alone doesn't already prevent this. Dock
 * mode never needed it: it reflows instead of floating over anything.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { LumiModuleKey } from '../modules';
import {
  lumiDockLauncher,
  setLumiDockLauncherToggle,
  setLumiDockLauncherOpen,
  focusLumiDock,
} from './lumiDockLauncher';
import { launcherAvoidance } from '../feedback/formActionBar';
import LumiIcon from '../icons/LumiIcon.vue';

type ShellMode = 'float' | 'dock';
type ShellState = 'expanded' | 'minimized';

const props = withDefaults(
  defineProps<{
    /** Suite/module accent, mirrored onto the root as data-module so the panel
     *  resolves the right accent even if the shell is ever mounted outside a
     *  module-scoped ancestor. Redundant but harmless when it already sits
     *  inside one (the usual AppShell case). */
    module?: LumiModuleKey;
    title?: string;
    /** localStorage key for the persisted mode; scope it per tenant+user.
     *  Omit to disable persistence (state still works in-memory). */
    storageKey?: string;
    initialMode?: ShellMode;
    launcherLabel?: string;
    newSessionLabel?: string;
    minimizeLabel?: string;
    dockLabel?: string;
    floatLabel?: string;
    resizeLabel?: string;
  }>(),
  {
    title: 'lumi',
    initialMode: 'float',
    launcherLabel: 'lumi',
    newSessionLabel: 'Neue Unterhaltung',
    minimizeLabel: 'Minimieren',
    dockLabel: 'Andocken',
    floatLabel: 'Schweben lassen',
    resizeLabel: 'Breite anpassen',
  },
);

const emit = defineEmits<{
  'update:mode': [mode: ShellMode];
  'update:state': [state: ShellState];
  /** The explicit clear/start-fresh action (header button). X never clears. */
  'new-session': [];
}>();

const MOBILE_QUERY = '(max-width: 767px)';
/** Bounds of the user-set panel width, in px (#539). */
const LUMI_MIN_W = 280;
const LUMI_MAX_W = 640;
const KEYBOARD_STEP = 16;
/** How far the pointer travels on the head before the window moves, in px. */
const MOVE_THRESHOLD = 3;

const mode = ref<ShellMode>(props.initialMode);
const state = ref<ShellState>('minimized');
const isMobile = ref(false);

const panelRef = ref<HTMLElement | null>(null);
let lastFocused: HTMLElement | null = null;
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input:not([type="hidden"]), select, [tabindex]:not([tabindex="-1"])';

// The sidebar's account-row button is the ONE entry point, in both modes and
// at every breakpoint, and it does not go away while the panel is open — that is what makes a second click
// close Lumi again. So the bridge stays visible for
// as long as this shell is mounted (onBeforeUnmount clears it when AI
// availability flips off mid-session), and carries the expanded/minimized
// state so the button can render as pressed rather than silently toggling
// something the user cannot see.
watch(state, (v) => { lumiDockLauncher.expanded = v === 'expanded'; }, { immediate: true });
watch(() => props.launcherLabel, (v) => { lumiDockLauncher.label = v; }, { immediate: true });

// Float mode only (outcome invariant 8 — dock already reflows and never
// overlaps anything). Only float-scoped CSS rules read this custom property,
// so setting it unconditionally is harmless in dock mode; it just goes
// unused there.
// The user-set width overrides both modes' width tokens on the root, capped at
// half the viewport so a width chosen on a big screen never swallows the page
// on a smaller one. Unset, the tokens' own defaults apply.
const width = ref<number | null>(null);
const resizing = ref(false);
// The phone sheet stands in the part of the screen the keyboard leaves free (the
// visual viewport), so its head stays in sight and the composer sits on top of
// the keyboard, as in a messaging app. Without it Safari slid the whole page up
// under the keyboard to show the field, and the sheet's head went off the top.
const sheetViewport = ref<{ top: number; height: number } | null>(null);
// Where the user has dragged the float window, as offsets from the viewport's
// right and top edges. Null is the start position in the bottom-right corner.
const placement = ref<{ right: number; top: number } | null>(null);
const moving = ref(false);
const rootStyle = computed(() => {
  const style: Record<string, string> = {
    '--lumi-shell-avoid': `${launcherAvoidance.reservedBottomPx}px`,
  };
  if (width.value != null) {
    const w = `min(${width.value}px, 50vw)`;
    style['--lumi-dock-w'] = w;
    style['--lumi-shell-panel-w'] = w;
  }
  if (placement.value) {
    style['--lumi-shell-right'] = `${placement.value.right}px`;
    style['--lumi-shell-top'] = `${placement.value.top}px`;
  }
  if (sheetViewport.value) {
    style['--lumi-sheet-top'] = `${sheetViewport.value.top}px`;
    style['--lumi-sheet-h'] = `${sheetViewport.value.height}px`;
  }
  return style;
});

const widthKey = computed(() => (props.storageKey ? `${props.storageKey}:width` : null));

function clampWidth(px: number): number {
  const max = typeof window !== 'undefined' ? Math.min(LUMI_MAX_W, window.innerWidth / 2) : LUMI_MAX_W;
  return Math.round(Math.min(Math.max(px, LUMI_MIN_W), Math.max(max, LUMI_MIN_W)));
}

function readStoredWidth(): number | null {
  if (!widthKey.value || typeof localStorage === 'undefined') return null;
  try {
    const v = Number(localStorage.getItem(widthKey.value));
    return Number.isFinite(v) && v > 0 ? Math.min(Math.max(v, LUMI_MIN_W), LUMI_MAX_W) : null;
  } catch {
    return null;
  }
}

function persistWidth() {
  if (!widthKey.value || typeof localStorage === 'undefined') return;
  try {
    if (width.value == null) localStorage.removeItem(widthKey.value);
    else localStorage.setItem(widthKey.value, String(width.value));
  } catch {
    /* quota / denied — non-fatal */
  }
}

function currentWidth(): number {
  return width.value ?? panelRef.value?.getBoundingClientRect().width ?? LUMI_MIN_W;
}

// The panel is anchored on the right in both modes, so dragging the handle
// left widens it.
let dragStartX = 0;
let dragStartW = 0;
function onResizeStart(e: PointerEvent) {
  if (e.button !== 0) return;
  e.preventDefault();
  dragStartX = e.clientX;
  dragStartW = currentWidth();
  resizing.value = true;
  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
}
function onResizeMove(e: PointerEvent) {
  if (!resizing.value) return;
  width.value = clampWidth(dragStartW + (dragStartX - e.clientX));
}
function onResizeEnd(e: PointerEvent) {
  if (!resizing.value) return;
  resizing.value = false;
  (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  persistWidth();
}
function onResizeKey(e: KeyboardEvent) {
  const delta = e.key === 'ArrowLeft' ? KEYBOARD_STEP : e.key === 'ArrowRight' ? -KEYBOARD_STEP : 0;
  if (!delta) return;
  e.preventDefault();
  width.value = clampWidth(currentWidth() + delta);
  persistWidth();
}
function resetWidth() {
  width.value = null;
  persistWidth();
}

function canMove(): boolean {
  return mode.value === 'float' && !isMobile.value;
}

// The whole window stays inside the viewport, so its head can always be taken
// hold of again.
function clampPlacement(right: number, top: number): { right: number; top: number } {
  const box = panelRef.value?.getBoundingClientRect();
  const maxRight = Math.max(0, window.innerWidth - (box?.width ?? 0));
  const maxTop = Math.max(0, window.innerHeight - (box?.height ?? 0));
  return {
    right: Math.round(Math.min(Math.max(right, 0), maxRight)),
    top: Math.round(Math.min(Math.max(top, 0), maxTop)),
  };
}

// After the viewport shrinks, the window grows wider, or it reopens, it may
// reach past an edge; it is pulled back in.
function keepPlacementInView() {
  if (!placement.value || !panelRef.value || !canMove()) return;
  placement.value = clampPlacement(placement.value.right, placement.value.top);
}

let moveStart: { x: number; y: number; right: number; top: number } | null = null;
function onMoveStart(e: PointerEvent) {
  if (e.button !== 0 || !canMove() || !panelRef.value) return;
  if ((e.target as Element).closest('button')) return;
  e.preventDefault();
  const box = panelRef.value.getBoundingClientRect();
  moveStart = { x: e.clientX, y: e.clientY, right: window.innerWidth - box.right, top: box.top };
  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
}
function onMove(e: PointerEvent) {
  if (!moveStart) return;
  const dx = e.clientX - moveStart.x;
  const dy = e.clientY - moveStart.y;
  // A click on the head is not a move: the window leaves its place only once
  // the pointer has travelled.
  if (!moving.value && Math.hypot(dx, dy) < MOVE_THRESHOLD) return;
  moving.value = true;
  placement.value = clampPlacement(moveStart.right - dx, moveStart.top + dy);
}
function onMoveEnd(e: PointerEvent) {
  if (!moveStart) return;
  moveStart = null;
  moving.value = false;
  (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
}
function resetPlacement(e: MouseEvent) {
  if (!canMove() || (e.target as Element).closest('button')) return;
  placement.value = null;
}
watch([width, mode, state], () => nextTick(keepPlacementInView));

function readStoredMode(): ShellMode | null {
  if (!props.storageKey || typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(props.storageKey);
    return v === 'float' || v === 'dock' ? v : null;
  } catch {
    return null;
  }
}

function persistMode(m: ShellMode) {
  if (!props.storageKey || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(props.storageKey, m);
  } catch {
    /* quota / denied — non-fatal */
  }
}

function expand() {
  if (state.value === 'expanded') return;
  lastFocused = typeof document !== 'undefined' ? (document.activeElement as HTMLElement) : null;
  state.value = 'expanded';
  emit('update:state', state.value);
  // Move focus into the panel so keyboard users land in the assistant. On the
  // phone sheet it lands on the sheet itself: Safari draws the focus ring on a
  // button focused after a tap, so the first head button ("new conversation")
  // opened ringed in black, as if it had been pressed. Tab still starts there.
  nextTick(() => {
    const panel = panelRef.value;
    if (!panel) return;
    const first = isMobile.value ? null : panel.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel).focus?.({ preventScroll: true });
  });
}
function minimize() {
  if (state.value === 'minimized') return;
  state.value = 'minimized';
  emit('update:state', state.value);
  // Return focus to the launcher so it isn't lost on the collapsed body. It
  // is Sidebar's account-row button, reached through the bridge — the one
  // entry point in both modes. On mobile that button sits in the off-canvas drawer, so
  // focusing it would land the user on something they cannot see; there
  // focus goes back to wherever it came from instead.
  nextTick(() => {
    if (!isMobile.value) {
      focusLumiDock();
      return;
    }
    lastFocused?.focus?.();
  });
}
function toggle() {
  if (state.value === 'expanded') minimize();
  else expand();
}
function setMode(m: ShellMode) {
  if (mode.value !== m) {
    mode.value = m;
    emit('update:mode', m);
    persistMode(m);
  }
}
function toggleMode() {
  setMode(mode.value === 'float' ? 'dock' : 'float');
}
/**
 * Expand in `m` unless the person has already chosen a mode (a stored one wins).
 * For the one unasked open, Lumi's first contact (issue 993): it opens in the
 * dock, so the page stays usable beside it instead of under a floating window.
 * The mode is stored like any other, so a module switch mid-conversation keeps
 * it; the person's next own choice replaces it.
 */
function expandPreferring(m: ShellMode) {
  if (!readStoredMode()) setMode(m);
  expand();
}
function newSession() {
  emit('new-session');
}

let mql: MediaQueryList | null = null;
function onMqlChange(e: MediaQueryListEvent) {
  isMobile.value = e.matches;
}

function trackSheetViewport() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  sheetViewport.value = vv && isMobile.value && state.value === 'expanded' ? { top: vv.offsetTop, height: vv.height } : null;
}
watch(
  () => isMobile.value && state.value === 'expanded',
  (sheet) => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    if (sheet) {
      vv.addEventListener('resize', trackSheetViewport);
      vv.addEventListener('scroll', trackSheetViewport);
    } else {
      vv.removeEventListener('resize', trackSheetViewport);
      vv.removeEventListener('scroll', trackSheetViewport);
    }
    trackSheetViewport();
  },
);

// Escape minimizes when expanded; while the mobile sheet is up (effectively
// modal) Tab is trapped inside the panel.
function onKeydown(e: KeyboardEvent) {
  if (state.value !== 'expanded') return;
  if (e.key === 'Escape') {
    minimize();
    return;
  }
  if (e.key === 'Tab' && isMobile.value && panelRef.value) {
    const nodes = Array.from(panelRef.value.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

onMounted(() => {
  const stored = readStoredMode();
  if (stored) mode.value = stored;
  width.value = readStoredWidth();
  if (typeof window !== 'undefined' && window.matchMedia) {
    mql = window.matchMedia(MOBILE_QUERY);
    isMobile.value = mql.matches;
    mql.addEventListener('change', onMqlChange);
  }
  if (typeof document !== 'undefined') document.addEventListener('keydown', onKeydown);
  if (typeof window !== 'undefined') window.addEventListener('resize', keepPlacementInView);
  setLumiDockLauncherToggle(toggle);
  setLumiDockLauncherOpen(() => expandPreferring('dock'));
  lumiDockLauncher.visible = true;
});

onBeforeUnmount(() => {
  if (mql) mql.removeEventListener('change', onMqlChange);
  window.visualViewport?.removeEventListener('resize', trackSheetViewport);
  window.visualViewport?.removeEventListener('scroll', trackSheetViewport);
  if (typeof document !== 'undefined') document.removeEventListener('keydown', onKeydown);
  if (typeof window !== 'undefined') window.removeEventListener('resize', keepPlacementInView);
  setLumiDockLauncherToggle(null);
  setLumiDockLauncherOpen(null);
  lumiDockLauncher.visible = false;
  lumiDockLauncher.expanded = false;
});

// storageKey can arrive after mount (tenant/user resolve async) — re-read then.
watch(
  () => props.storageKey,
  () => {
    const stored = readStoredMode();
    if (stored) mode.value = stored;
    width.value = readStoredWidth();
  },
);

// Let the app drive (expand on a help event, pause polling when minimized, …)
// without owning the state machine.
defineExpose({ expand, expandPreferring, minimize, toggle, toggleMode, setMode });
</script>

<template>
  <div
    class="lumi-shell"
    :data-mode="mode"
    :data-state="state"
    :data-module="module || undefined"
    :data-resizing="resizing || undefined"
    :data-placed="placement ? '' : undefined"
    :data-moving="moving || undefined"
    :style="rootStyle"
  >
    <!-- Minimized renders nothing here, in either mode: the one entry point is
         the account row's Lumi button (lumiDockLauncher bridge). -->

    <!-- Expanded panel: shared by float window, dock and mobile sheet. -->
    <section v-if="state === 'expanded'" ref="panelRef" tabindex="-1" class="lumi-shell__panel" :aria-label="title">
      <header
        class="lumi-shell__head"
        @pointerdown="onMoveStart"
        @pointermove="onMove"
        @pointerup="onMoveEnd"
        @pointercancel="onMoveEnd"
        @dblclick="resetPlacement"
      >
        <div class="lumi-shell__brand">
          <span class="lumi-shell__brand-icon" aria-hidden="true">
            <LumiIcon name="lumi" width="22" height="22" />
          </span>
          <slot name="title"><span class="lumi-shell__title">{{ title }}</span></slot>
        </div>
        <div class="lumi-shell__actions">
          <button
            type="button"
            class="lumi-shell__icon-btn"
            :aria-label="newSessionLabel"
            :title="newSessionLabel"
            @click="newSession"
          >
            <!-- a new conversation -->
            <LumiIcon name="write" width="18" height="18" stroke-width="1.8" />
          </button>
          <!-- Float and dock are one full-screen sheet on a phone, so there is
               nothing for this button to switch there. -->
          <button
            v-if="!isMobile"
            type="button"
            class="lumi-shell__icon-btn"
            :aria-label="mode === 'float' ? dockLabel : floatLabel"
            :title="mode === 'float' ? dockLabel : floatLabel"
            @click="toggleMode"
          >
            <!-- to dock: panel-right -->
            <LumiIcon v-if="mode === 'float'" name="dock" width="18" height="18" stroke-width="1.8" />
            <!-- to float: pop-out window -->
            <LumiIcon v-else name="float" width="18" height="18" stroke-width="1.8" />
          </button>
          <button
            type="button"
            class="lumi-shell__icon-btn"
            :aria-label="minimizeLabel"
            :title="minimizeLabel"
            @click="minimize"
          >
            <LumiIcon name="remove" width="18" height="18" stroke-width="2" />
          </button>
        </div>
      </header>

      <div class="lumi-shell__body"><slot name="body" /></div>

      <footer v-if="$slots.footer" class="lumi-shell__foot"><slot name="footer" /></footer>
      <!-- Last in the panel so it paints over the head and foot at the same
           layer. Not on the mobile sheet, which is always full-screen (and
           whose Tab trap must not land on it). -->
      <div
        v-if="!isMobile"
        class="lumi-shell__resize"
        role="separator"
        aria-orientation="vertical"
        tabindex="0"
        :aria-label="resizeLabel"
        :title="resizeLabel"
        :aria-valuenow="width ?? undefined"
        :aria-valuemin="LUMI_MIN_W"
        :aria-valuemax="LUMI_MAX_W"
        @pointerdown="onResizeStart"
        @pointermove="onResizeMove"
        @pointerup="onResizeEnd"
        @pointercancel="onResizeEnd"
        @keydown="onResizeKey"
        @dblclick="resetWidth"
      />
    </section>
  </div>
</template>

<style>
/* The root reserves the dock width in-flow (dock mode) or collapses to 0 so the
   overlay reserves nothing (float mode). The transition is what makes content
   reflow animate; AppShell stays dumb and just lets this flex child size. */
.lumi-shell {
  height: 100%;
  flex-shrink: 0;
  font-family: var(--font-body);
  transition: width var(--dur-slow) var(--ease-out);
}
.lumi-shell[data-mode='float'] { width: 0; }
/* Docked, the root clips what it has not grown into yet (issue 1026). The panel
   rides its left edge, so opening slides it in from the right while the width
   eases open. Unclipped, the full-width panel hung past the viewport for the
   length of the transition and the shell's overflow-hidden box scrolled sideways
   to show it: the panel popped up whole and the sidebar slid back in from the
   left. `clip`, not `hidden`: a box that is no scroll container cannot be
   scrolled into view either. */
.lumi-shell[data-mode='dock'] { overflow-x: clip; }
/* Docked: the 320px peer panel plus its canvas margin on the right; the gap to
   the main panel is that panel's own right margin. Minimized reserves nothing. */
.lumi-shell[data-mode='dock'][data-state='expanded'] { width: calc(var(--lumi-dock-w) + var(--panel-gap)); }
.lumi-shell[data-mode='dock'][data-state='minimized'] { width: 0; }
/* Mid-drag the width follows the pointer directly — no easing lag. */
.lumi-shell[data-resizing] { transition: none; user-select: none; }

/* ── Expanded panel: the main content panel's edge, on its own lavender ── */
.lumi-shell__panel {
  display: flex;
  flex-direction: column;
  background: var(--lumi-panel);
  color: var(--text-body);
  border: 1px solid var(--panel-edge);
  border-radius: var(--panel-radius);
  overflow: hidden;
}
.lumi-shell[data-mode='float'] .lumi-shell__panel {
  position: fixed;
  right: var(--panel-gap);
  /* Shifts clear of an open form's action bar (outcome invariant 8) — see
     formActionBar.ts. `--lumi-shell-avoid` is 0px whenever nothing is
     registered, so this is the plain panel-gap offset the rest of the time. */
  bottom: calc(var(--panel-gap) + var(--lumi-shell-avoid, 0px));
  z-index: var(--layer-popover);
  width: var(--lumi-shell-panel-w);
  max-width: calc(100vw - 2 * var(--panel-gap));
  height: var(--lumi-shell-panel-h);
  /* Top edge: the panel-gap, or — under an AppShell banner strip — the bottom of
     that strip (`--lumi-shell-top-clear`, AppShell), so it never covers the strip's
     button. The bottom offset (gap + the form action bar's `--lumi-shell-avoid`) is
     subtracted too: the panel is bottom-anchored, so a lifted bottom pushes the top
     edge up unless the height gives way (it left the viewport's top by 12px). */
  max-height: calc(100vh - var(--lumi-shell-top-clear, var(--panel-gap)) - var(--panel-gap) - var(--lumi-shell-avoid, 0px));
  box-shadow: var(--shadow-modal);
}
/* Moved by its head: the window stands where the user put it, still anchored
   on the right so the resize handle widens it leftwards. The form action bar's
   avoidance is for the start corner only. */
.lumi-shell[data-mode='float'][data-placed] .lumi-shell__panel {
  top: var(--lumi-shell-top);
  right: var(--lumi-shell-right);
  bottom: auto;
}
.lumi-shell[data-mode='float'] .lumi-shell__head { cursor: grab; user-select: none; touch-action: none; }
.lumi-shell[data-moving] .lumi-shell__head { cursor: grabbing; }
.lumi-shell[data-moving] { user-select: none; }
.lumi-shell[data-mode='dock'] .lumi-shell__panel {
  position: relative;
  width: var(--lumi-dock-w);
  height: calc(100% - 2 * var(--panel-gap));
  margin: var(--panel-gap) var(--panel-gap) var(--panel-gap) 0;
  box-shadow: var(--shadow-panel);
}

/* The resize handle: a thin strip on the left edge, in front of the head, the
   thread and the foot (it is the panel's last child). The cursor is the left-right arrow; on hover or focus a short
   grip line shows where to take hold (#539). */
.lumi-shell__resize {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 8px;
  z-index: var(--layer-raised);
  cursor: ew-resize;
  touch-action: none;
  outline: none;
}
.lumi-shell__resize::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 2px;
  width: 3px;
  height: 40px;
  transform: translateY(-50%);
  border-radius: var(--radius-pill);
  background: var(--panel-edge);
  opacity: 0;
  transition: opacity var(--dur-fast) var(--ease-standard);
}
.lumi-shell__resize:hover::after,
.lumi-shell__resize:focus-visible::after,
.lumi-shell[data-resizing] .lumi-shell__resize::after { opacity: 1; background: var(--text-tertiary); }

/* Head on the panel colour, in front of the thread, fading into it (spec §6). */
.lumi-shell__head {
  position: relative;
  z-index: var(--layer-raised);
  height: var(--topbar-h);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 0 var(--space-3) 0 var(--space-4);
  background: var(--lumi-panel);
}
.lumi-shell__head::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: var(--fade-h);
  background: linear-gradient(to bottom, var(--lumi-panel), transparent);
  pointer-events: none;
}
.lumi-shell__brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
/* The sparkle square: ink with the panel-coloured glyph. */
.lumi-shell__brand-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--btn-primary-bg);
  color: var(--btn-primary-fg);
  flex-shrink: 0;
}
.lumi-shell__title {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--text-heading);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lumi-shell__actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
.lumi-shell__icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: none;
  color: var(--text-secondary);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-standard), background var(--dur-fast) var(--ease-standard);
}
.lumi-shell__icon-btn:hover { color: var(--text-heading); background: var(--surface-wash); }

.lumi-shell__body { flex: 1; min-height: 0; overflow-y: auto; }

/* Foot in front of the thread too: the composer card the app slots in floats,
   and the history fades out beneath it. */
.lumi-shell__foot { position: relative; z-index: var(--layer-raised); flex-shrink: 0; }
.lumi-shell__foot::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: var(--fade-h);
  background: linear-gradient(to top, var(--lumi-panel), transparent);
  pointer-events: none;
}

/* ── Responsive: dock degrades to a full-screen sheet; the root reserves
      nothing (the sheet is fixed). ── */
@media (max-width: 767px) {
  .lumi-shell { width: 0 !important; }
  .lumi-shell__panel:focus { outline: none; }
  .lumi-shell__panel {
    position: fixed !important;
    top: var(--lumi-sheet-top) !important;
    right: 0 !important;
    bottom: auto !important;
    left: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    height: var(--lumi-sheet-h) !important;
    max-height: none !important;
    overscroll-behavior: contain;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    /* Clear of the notch and the home indicator (AppShell's insets, the same
       `viewport-fit=cover`): the sheet is fixed over the whole screen. */
    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
    z-index: var(--layer-popover);
  }
  /* A finger, not a pointer: the head's buttons grow to a 40px target. */
  .lumi-shell__icon-btn { width: 40px; height: 40px; }
  /* The sheet fills the screen, so there is nowhere to move it. */
  .lumi-shell[data-mode='float'] .lumi-shell__head { cursor: auto; touch-action: auto; }
}
</style>
